import { matchesOrigin } from "./embed";

export const CONNECT_AUTH_READY = "connect:auth-ready";
export const CONNECT_AUTH_TOKEN = "connect:auth-token";
export const CONNECT_AUTH_EXPIRED = "connect:auth-expired";
export const CONNECT_AUTH_OK = "connect:auth-ok";
export const CONNECT_AUTH_ERROR = "connect:auth-error";
export const CONNECT_AUTH_LOGOUT = "connect:auth-logout";
export const CONNECT_AUTH_LOGOUT_OK = "connect:auth-logout-ok";

interface ConnectAuthMessage {
    type?: string;
    accessToken?: string;
    token?: string;
    tenantId?: string;
    reason?: string;
    correlationId?: string;
    detail?: string;
}

export interface ConnectHandshakeConfig {
    connectOrigin: string;
    parentOrigin: string;
    returnUrl: string;
    rootUrl: string;
}

export interface ConnectShellBridgeConfig {
    connectOrigin: string | null | undefined;
    parentOrigin: string | null | undefined;
    rootUrl: string;
    onSessionEnded?: () => void;
}

const READY_RETRY_INTERVAL_MS = 2000;
const READY_MAX_ATTEMPTS = 5;
const EXCHANGE_MAX_ATTEMPTS = 3;

export const resolveConnectParentOrigin = (
    allowlist: string | null | undefined,
    parentOriginHint: string | null | undefined
): string | null => {
    const tokens = (allowlist || "").split(/\s+/).filter((t: string) => t.startsWith("http"));
    if (!tokens.length) return null;

    const candidates: string[] = [];
    if (parentOriginHint) candidates.push(parentOriginHint);
    if (document.referrer) candidates.push(document.referrer);

    for (const candidate of candidates) {
        let origin: string;
        try {
            origin = new URL(candidate).origin;
        } catch (e) {
            continue;
        }
        if (tokens.some((t) => matchesOrigin(origin, t))) return origin;
    }
    return null;
};

const buildUrl = (rootUrl: string, path: string): string => {
    const base = rootUrl && rootUrl.length ? rootUrl : "/";
    return base.charAt(base.length - 1) === "/" ? base + path : base + "/" + path;
};

const appendParentOrigin = (returnUrl: string, parentOrigin: string): string => {
    if (!parentOrigin || returnUrl.includes("parentOrigin=")) return returnUrl;
    const hashIndex = returnUrl.indexOf("#");
    const base = hashIndex >= 0 ? returnUrl.slice(0, hashIndex) : returnUrl;
    const hash = hashIndex >= 0 ? returnUrl.slice(hashIndex) : "";
    const sep = base.includes("?") ? "&" : "?";
    return base + sep + "parentOrigin=" + encodeURIComponent(parentOrigin) + hash;
};

export const exchangeConnectSession = (rootUrl: string, accessToken: string, tenantId: string): Promise<string | undefined> => {
    return fetch(buildUrl(rootUrl, "api/ConnectShell/connect-session"), {
        method: "POST",
        credentials: "include",
        headers: { Authorization: "Bearer " + accessToken, "veson-tenant-id": tenantId }
    }).then((response: Response) => {
        if (!response.ok) throw new Error("connect-session exchange failed: " + response.status);
        if (typeof response.json !== "function") return undefined;
        return response.json().then(
            (body: { CompanyCode?: string } | null) => (body && body.CompanyCode) || undefined,
            () => undefined
        );
    });
};

export const clearConnectSession = (rootUrl: string): Promise<void> => {
    return fetch(buildUrl(rootUrl, "api/ConnectShell/connect-logout"), {
        method: "POST",
        credentials: "include",
        keepalive: true
    }).then((response: Response) => {
        if (!response.ok) throw new Error("connect-logout failed: " + response.status);
    });
};

// ---- Handshake (anonymous defer-auth shell) -------------------------------------------------

export const runConnectHandshake = (config: ConnectHandshakeConfig): void => {
    const targetOrigin = resolveConnectParentOrigin(config.connectOrigin, config.parentOrigin);
    if (!targetOrigin) return; // not framed by an allowed Connect origin; cannot proceed securely

    let settled = false;
    let stopped = false;
    let attempts = 0;
    let exchangeAttempts = 0;
    let retryTimer: number | undefined;

    const stop = (): void => {
        stopped = true;
        if (retryTimer !== undefined) {
            window.clearTimeout(retryTimer);
            retryTimer = undefined;
        }
    };

    const sendAuthOk = (correlationId: string | undefined, company?: string): void => {
        const message: { type: string; correlationId: string | undefined; company?: string } =
            { type: CONNECT_AUTH_OK, correlationId };
        if (company) message.company = company;
        window.parent.postMessage(message, targetOrigin);
    };

    const sendAuthError = (reason: string, correlationId: string | undefined): void => {
        window.parent.postMessage({ type: CONNECT_AUTH_ERROR, reason, correlationId }, targetOrigin);
    };

    const sendReady = (): void => {
        if (retryTimer !== undefined) {
            window.clearTimeout(retryTimer);
            retryTimer = undefined;
        }
        if (stopped || settled) return;
        if (attempts >= READY_MAX_ATTEMPTS) { stop(); return; }
        attempts += 1;
        window.parent.postMessage({ type: CONNECT_AUTH_READY }, targetOrigin);
        retryTimer = window.setTimeout(sendReady, READY_RETRY_INTERVAL_MS);
    };

    window.addEventListener("message", (event: MessageEvent) => {
        if (event.source !== window.parent || event.origin !== targetOrigin) return;
        const data: ConnectAuthMessage = event.data || {};

        if (data.type === CONNECT_AUTH_TOKEN) {
            const accessToken = data.accessToken || data.token;
            if (settled || !accessToken || !data.tenantId) return;
            const correlationId = data.correlationId;
            settled = true;
            stop();
            exchangeAttempts += 1;
            exchangeConnectSession(config.rootUrl, accessToken, data.tenantId)
                .then((company) => {
                    sendAuthOk(correlationId, company);
                    window.location.replace(appendParentOrigin(config.returnUrl, targetOrigin));
                })
                .catch(() => {
                    if (exchangeAttempts >= EXCHANGE_MAX_ATTEMPTS) {
                        stop();
                        sendAuthError("session-failed", correlationId);
                        return;
                    }
                    settled = false;
                    stopped = false;
                    sendReady();
                });
        } else if (data.type === CONNECT_AUTH_ERROR) {
            if (data.reason === "no-session") stop();
        }
    });

    sendReady();
};

// ---- Authenticated shell bridge -------------------------------------------------------------

export interface ConnectShellBridge {
    handleSessionExpiry: () => void;
    handleCompanySuperseded: (company: string | null | undefined) => void;
}

export const initConnectShellBridge = (config: ConnectShellBridgeConfig): ConnectShellBridge | null => {
    const targetOrigin = resolveConnectParentOrigin(config.connectOrigin, config.parentOrigin);
    if (!targetOrigin || window.parent === window) {
        return null;
    }

    let latestAccessToken: string | null = null;
    let latestTenantId: string | null = null;
    let latestCorrelationId: string | undefined;
    let recovering = false;
    let exchangeFailures = 0;
    let superseded = false;
    let pendingTenantSwitch = false;
    let teardownPromise: Promise<void> | null = null;

    const reExchangeAndReload = (accessToken: string, tenantId: string, correlationId: string | undefined): void => {
        exchangeConnectSession(config.rootUrl, accessToken, tenantId)
            .then((company) => {
                const message: { type: string; correlationId: string | undefined; company?: string } =
                    { type: CONNECT_AUTH_OK, correlationId };
                if (company) message.company = company;
                window.parent.postMessage(message, targetOrigin);
                window.location.reload();
            })
            .catch(() => {
                recovering = false;
                exchangeFailures += 1;
                if (exchangeFailures >= EXCHANGE_MAX_ATTEMPTS) {
                    window.parent.postMessage({ type: CONNECT_AUTH_ERROR, reason: "session-failed", correlationId }, targetOrigin);
                }
            });
    };

    const handleCompanySuperseded = (company: string | null | undefined): void => {
        if (superseded) return;
        superseded = true;
        window.parent.postMessage({ type: CONNECT_AUTH_ERROR, reason: "company-superseded", company }, targetOrigin);
    };

    const handleSessionExpiry = (): void => {
        if (superseded || recovering || pendingTenantSwitch) return;
        recovering = true;
        window.parent.postMessage({ type: CONNECT_AUTH_EXPIRED }, targetOrigin);
        if (latestAccessToken && latestTenantId) {
            reExchangeAndReload(latestAccessToken, latestTenantId, latestCorrelationId);
        }
    };

    const handleTokenMessage = (data: ConnectAuthMessage): void => {
        const accessToken = data.accessToken || data.token;
        if (!accessToken || !data.tenantId) return;
        if (superseded) return;
        latestAccessToken = accessToken;
        latestTenantId = data.tenantId;
        latestCorrelationId = data.correlationId;
        if (pendingTenantSwitch) {
            pendingTenantSwitch = false;
            recovering = true;
            const tenantId = data.tenantId;
            const correlationId = data.correlationId;
            const proceed = (): void => reExchangeAndReload(accessToken, tenantId, correlationId);
            const afterTeardown = teardownPromise ?? Promise.resolve();
            afterTeardown.then(proceed, proceed);
            return;
        }

        if (recovering) reExchangeAndReload(accessToken, data.tenantId, data.correlationId);
    };

    const handleLogoutMessage = (data: ConnectAuthMessage): void => {
        const correlationId = data.correlationId;
        const isSignOut = data.reason !== "tenant-switch";
        latestAccessToken = null;
        latestTenantId = null;
        latestCorrelationId = undefined;
        recovering = false;
        exchangeFailures = 0;
        if (!isSignOut) {
            pendingTenantSwitch = true;
            superseded = false;
        }
        const finish = (): void => {
            if (isSignOut && config.onSessionEnded) config.onSessionEnded();
        };
        const teardown = clearConnectSession(config.rootUrl)
            .then(() => {
                window.parent.postMessage({ type: CONNECT_AUTH_LOGOUT_OK, correlationId }, targetOrigin);
                finish();
            })
            .catch(() => {
                window.parent.postMessage({ type: CONNECT_AUTH_ERROR, reason: "logout-failed", correlationId }, targetOrigin);
                finish();
            });
        if (!isSignOut) teardownPromise = teardown;
    };

    window.addEventListener("message", (event: MessageEvent) => {
        if (event.source !== window.parent || event.origin !== targetOrigin) return;
        const data: ConnectAuthMessage = event.data || {};
        if (data.type === CONNECT_AUTH_TOKEN) handleTokenMessage(data);
        else if (data.type === CONNECT_AUTH_LOGOUT) handleLogoutMessage(data);
    });

    return { handleSessionExpiry, handleCompanySuperseded };
};

export const CONNECT_COMPANY_PIN_PARAM = "connectCompany";
const CONNECT_COMPANY_MISMATCH_MARKER = "connectCompanyMismatch";

let activeConnectCompanyPin: string | null = null;
let activeCompanySupersededNotifier: ((company: string | null | undefined) => void) | null = null;

export const registerConnectExportContext = (
    companyPin: string | null | undefined,
    onCompanySuperseded: (company: string | null | undefined) => void
): void => {
    activeConnectCompanyPin = companyPin || null;
    activeCompanySupersededNotifier = onCompanySuperseded;
};

export const getConnectExportCompanyPin = (): string | null => activeConnectCompanyPin;

export const handleConnectExportResponse = (bodyText: string | null | undefined): boolean => {
    if (!bodyText || !activeCompanySupersededNotifier) return false;
    if (bodyText.indexOf(CONNECT_COMPANY_MISMATCH_MARKER) < 0) return false;
    let company: string | null = null;
    try {
        const parsed = JSON.parse(bodyText);
        if (!parsed || !parsed[CONNECT_COMPANY_MISMATCH_MARKER]) return false;
        company = parsed.company || null;
    } catch (e) {
        return false;
    }
    activeCompanySupersededNotifier(company);
    return true;
};
